﻿/*
Popovers: sometimes it happens that they open on the wrong side, wrong dimension, etc. As a workaround I was used to
wrap content in some divs with a minimal dimensions. I just figured out that the real issue was produced by the lazy
loading in VueRegistry. So whenever needed we can use normal import in VueRegistry instead of lazy import.
To give more context VueRegistry was created in order to avoid importing components everytime inside files.
The problem of importing everything at once is that it creates a big file when building for production.
So to solve this I've used lazy loading (defineAsyncComponent), which doesn't bundle all the code together of those
lazy loaded files. They are dynamically loaded when actually used.
 */

import {defineAsyncComponent} from "vue";

// services
import router from "./router";
import store from "./store/index";
import ToastService from "primevue/toastservice";
import { vueQueryPlugin, vueQueryOptions } from "@/src/queries/client";
import ConfirmationService from "primevue/confirmationservice";

// directives
import Tooltip from "@/src/utils/TooltipOverride";
import SafeHtml from "@/src/utils/SafeHtmlDirective";
import ShowWhitespace from "@/src/utils/ShowWhitespaceDirective";
import StyleClass from "primevue/styleclass";
import Ripple from "primevue/ripple";
import OverlayBadge from "primevue/overlaybadge";

// components
const CaseTableSelector = defineAsyncComponent(() =>
    import("@/src/shared-components/CaseTableSelector")
);
const CustomForm = defineAsyncComponent(() =>
    import("@/src/shared-components/CustomForm")
);
const DatamodelSelector = defineAsyncComponent(() =>
    import("@/src/shared-components/DatamodelSelector")
);
const FloatingDialog = defineAsyncComponent(() =>
    import("@/src/shared-components/FloatingDialog")
);

import FormField from "@/src/shared-components/FormField.vue";

const InputTags = defineAsyncComponent(() =>
    import("@/src/shared-components/InputTags")
);
const LabeledToggleSwitch = defineAsyncComponent(() =>
    import("@/src/shared-components/LabeledToggleSwitch")
);
const LoadingState = defineAsyncComponent(() =>
    import("@/src/shared-components/LoadingState")
);
const PermissionDialog = defineAsyncComponent(() =>
    import("@/src/shared-components/PermissionDialog")
);
const PqlEditor = defineAsyncComponent(() =>
    import("@/src/shared-components/PqlEditor")
);
const ProgressBar = defineAsyncComponent(() => import("@/src/shared-components/ProgressBar"));
const StaticImage = defineAsyncComponent(() =>
    import("@/src/shared-components/StaticImage")
);
const TreeNavigation = defineAsyncComponent(() =>
    import("@/src/shared-components/TreeNavigation")
);

const UnsavedChanges = defineAsyncComponent(() =>
    import("@/src/shared-components/UnsavedChanges")
);

const VersionPublish = defineAsyncComponent(() =>
    import("@/src/shared-components/VersionPublish")
);

const Accordion = defineAsyncComponent(() => import("primevue/accordion"));
const AccordionPanel = defineAsyncComponent(() => import("primevue/accordionpanel"));
const AccordionHeader = defineAsyncComponent(() => import("primevue/accordionheader"));
const AccordionContent = defineAsyncComponent(() => import("primevue/accordioncontent"));
const Avatar = defineAsyncComponent(() => import("primevue/avatar"));
const AutoComplete = defineAsyncComponent(() =>
    import("primevue/autocomplete")
);
const Badge = defineAsyncComponent(() => import("primevue/badge"));
const Breadcrumb = defineAsyncComponent(() => import("primevue/breadcrumb"));
const Button = defineAsyncComponent(() => import("primevue/button"));
const Card = defineAsyncComponent(() => import("primevue/card"));
const Carousel = defineAsyncComponent(() => import("primevue/carousel"));
const Checkbox = defineAsyncComponent(() => import("primevue/checkbox"));
const Chip = defineAsyncComponent(() => import("primevue/chip"));
const ColorPicker = defineAsyncComponent(() => import("primevue/colorpicker"));
import Column from "primevue/column";

const ConfirmDialog = defineAsyncComponent(() =>
    import("primevue/confirmdialog")
);
const ConfirmPopup = defineAsyncComponent(() =>
    import("primevue/confirmpopup")
);
const ContextMenu = defineAsyncComponent(() => import("primevue/contextmenu"));
import DataTable from "primevue/datatable";

const DatePicker = defineAsyncComponent(() => import("primevue/datepicker"));
const Dialog = defineAsyncComponent(() => import("primevue/dialog"));
const Divider = defineAsyncComponent(() => import("primevue/divider"));
const Drawer = defineAsyncComponent(() => import("primevue/drawer"));
const Editor = defineAsyncComponent(() => import("primevue/editor"));
const Fieldset = defineAsyncComponent(() => import("primevue/fieldset"));
const FileUpload = defineAsyncComponent(() => import("primevue/fileupload"));
const IconField = defineAsyncComponent(() => import("primevue/iconfield"));
const Image = defineAsyncComponent(() => import("primevue/image"));
const InputGroup = defineAsyncComponent(() => import("primevue/inputgroup"));
const InputIcon = defineAsyncComponent(() => import("primevue/inputicon"));
const InputNumber = defineAsyncComponent(() => import("primevue/inputnumber"));
import InputText from "primevue/inputtext";
const Knob = defineAsyncComponent(() => import("primevue/knob"));
const Listbox = defineAsyncComponent(() => import("primevue/listbox"));
const Menu = defineAsyncComponent(() => import("primevue/menu"));
const Menubar = defineAsyncComponent(() => import("primevue/menubar"));
const Message = defineAsyncComponent(() => import("primevue/message"));
const MultiSelect = defineAsyncComponent(() => import("primevue/multiselect"));
const OrganizationChart = defineAsyncComponent(() =>
    import("primevue/organizationchart")
);
const Paginator = defineAsyncComponent(() => import("primevue/paginator"));
const Popover = defineAsyncComponent(() => import("primevue/popover"));
const ProgressSpinner = defineAsyncComponent(() =>
    import("primevue/progressspinner")
);
const ScrollPanel = defineAsyncComponent(() => import("primevue/scrollpanel"));
const Select = defineAsyncComponent(() => import("primevue/select"));
const SelectButton = defineAsyncComponent(() =>
    import("primevue/selectbutton")
);
const Slider = defineAsyncComponent(() => import("primevue/slider"));
const SpeedDial = defineAsyncComponent(() => import("primevue/speeddial"));
const Stepper = defineAsyncComponent(() => import("primevue/stepper"));
const StepList = defineAsyncComponent(() => import("primevue/steplist"));
const StepPanels = defineAsyncComponent(() => import("primevue/steppanels"));
const StepItem = defineAsyncComponent(() => import("primevue/stepitem"));
const Step = defineAsyncComponent(() => import("primevue/step"));
const StepPanel = defineAsyncComponent(() => import("primevue/steppanel"));
// dynamic import does not work with these 2 components, no clue why
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";

const Tag = defineAsyncComponent(() => import("primevue/tag"));
const Textarea = defineAsyncComponent(() => import("primevue/textarea"));
const Toast = defineAsyncComponent(() => import("primevue/toast"));
const ToggleButton = defineAsyncComponent(() => import("primevue/togglebutton"));
const ToggleSwitch = defineAsyncComponent(() =>
    import("primevue/toggleswitch")
);
const Toolbar = defineAsyncComponent(() => import("primevue/toolbar"));
const Tree = defineAsyncComponent(() => import("primevue/tree"));
const TreeSelect = defineAsyncComponent(() => import("primevue/treeselect"));
const VirtualScroller = defineAsyncComponent(() => import("primevue/virtualscroller"));

export default {
    components: {
        CaseTableSelector,
        CustomForm,
        DatamodelSelector,
        FloatingDialog,
        FormField,
        InputTags,
        LabeledToggleSwitch,
        LoadingState,
        PermissionDialog,
        PqlEditor,
        ProgressBar,
        StaticImage,
        TreeNavigation,
        UnsavedChanges,
        VersionPublish,

        Accordion,
        AccordionPanel,
        AccordionHeader,
        AccordionContent,
        Avatar,
        AutoComplete,
        Badge,
        Breadcrumb,
        Button,
        Card,
        Carousel,
        Checkbox,
        Chip,
        Column,
        ColorPicker,
        ConfirmDialog,
        ConfirmPopup,
        ContextMenu,
        DataTable,
        DatePicker,
        Dialog,
        Divider,
        Drawer,
        Editor,
        Fieldset,
        FileUpload,
        IconField,
        Image,
        InputGroup,
        InputIcon,
        InputNumber,
        InputText,
        Knob,
        Listbox,
        Menu,
        Menubar,
        Message,
        MultiSelect,
        OrganizationChart,
        OverlayBadge,
        Paginator,
        Popover,
        ProgressSpinner,
        ScrollPanel,
        Select,
        SelectButton,
        Slider,
        SpeedDial,
        Stepper,
        StepList,
        StepPanels,
        StepItem,
        Step,
        StepPanel,
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        Tag,
        Textarea,
        Toast,
        ToggleButton,
        ToggleSwitch,
        Toolbar,
        Tree,
        TreeSelect,
        VirtualScroller,
    },
    directives: {
        tooltip: Tooltip,
        safeHtml: SafeHtml,
        styleclass: StyleClass,
        ripple: Ripple,
        showwhitespace: ShowWhitespace,
        inputchars: {
            mounted: (el, binding, vNode) => {
                if (!binding.value) {
                    return;
                }
                // Create a regular expression to match any character not present in binding.value
                // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp -- the pattern is a constant from component code, never user input
                const validCharsRegex = new RegExp(`[^${binding.value}]`, "g");
                // Attach an event listener to the 'beforeinput' event
                const inputField =
                    el.tagName === "INPUT" ? el : el.getElementsByTagName("input")[0];
                inputField.addEventListener("beforeinput", function (e) {
                    let value = e.target.value;
                    if (e.inputType === "insertText") {
                        // Get the combined value of the input field and the input event
                        value += e.data;
                    }

                    if (e.inputType === "insertFromPaste") {
                        value = e.data;
                    }

                    // Check if the combined value contains any invalid characters
                    if (event.target && validCharsRegex.test(value)) {
                        // Prevent the default behavior of the 'beforeinput' event
                        event.preventDefault();

                        // Replace all invalid characters with an empty string in the combined value
                        event.target.value = value.replace(validCharsRegex, "");

                        // Create a new 'input' event to manually trigger Vue reactivity
                        const newEvent = new Event("input", {
                            bubbles: true,
                            cancelable: true,
                        });
                        event.target.dispatchEvent(newEvent);
                    }
                });
            },
        },
    },
    services: {
        ConfirmationService,
        ToastService,
        router,
        store,
    },
    // VueQueryPlugin needs its options object, so it cannot go in the plain `services` list.
    plugins: [[vueQueryPlugin, vueQueryOptions]],
};
